// The Pomela landing — the public face at launch and the GTM centrepiece.
//
// This is what every logged-out / pre-launch visitor sees on a production
// host (pomela.app, *.pages.dev). It doubles as the waitlist gate while
// VITE_MAINTENANCE_MODE is on: the primary CTA captures emails, and the
// founder slips past it via the 5-tap wordmark (or ?dev=1). When the gate
// is flipped off at launch, the same page can point its CTA into the app.
//
// Positioning (locked, wedge C): MOBILE-NATIVE is the durable differentiator.
// Competitors generate websites; Pomela builds premium mobile-native PWAs you
// install to your home screen — offline, push, the Native Illusion. Voice:
// Israeli-startup — premium and confident, young and a little cheeky, never
// corporate filler. Design bar: Apple-grade.
//
// Self-contained: every style + animation is inlined in one <style> tag so
// the most important page in the product can never be broken by a regression
// elsewhere in the design system. The brand tokens (pink #F4477E, the warm→
// pink→violet gradient, Inter) are mirrored here verbatim for visual parity.
//
// Test contract (tests/playwright/launch-flip.spec.ts + ruthless-audit-v2):
// the root keeps `.mg-root` + an `aria-label` containing "waitlist"; the hero
// headline keeps `.mg-title`; the hero sub keeps `.mg-sub`; the primary CTA
// label keeps `.mg-cta-label`. Do not remove these hooks.

import { useEffect, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import {
  WAITLIST_ENDPOINT,
  WAITLIST_PERSIST_ENDPOINT,
  activateBypass,
  captureAttribution,
  isMaintenanceHost as _isMaintenanceHost,
  isValidEmail,
  pushTap,
  recordWaitlist
} from '../lib/maintenance-gate';
import { t } from '../lib/i18n';

export const isMaintenanceHost = _isMaintenanceHost;

type Status = 'idle' | 'loading' | 'success' | 'error';

// NOTE: this page shows ZERO app mockups by design — no phone frames, no
// screenshots, no app-like visuals. The promise is carried by copy + motion;
// the only hero visual is the Pomela brand mark. (Founder directive.)

export function MaintenanceGate(): JSX.Element {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [errMsg, setErrMsg] = useState(t('lp.errEmail'));
  const emailRef = useRef<HTMLInputElement>(null);
  const markWrapRef = useRef<HTMLDivElement>(null);

  // Persist dev-bypass via the secret five-tap on the wordmark. Five taps
  // within 2s sets the bypass flag and reloads — the founder's in-browser
  // kill switch without a redeploy. Tap array in a ref so a rapid burst
  // can't be dropped by a stale closure from setState batching.
  const tapsRef = useRef<number[]>([]);
  function onWordmarkTap() {
    const { taps, triggered } = pushTap(tapsRef.current);
    tapsRef.current = taps;
    if (triggered) activateBypass();
  }

  // Magnetic mascot — the pomelo tile tilts up to ±6° toward the cursor.
  // rAF-throttled, respects prefers-reduced-motion, skipped on touch.
  useEffect(() => {
    try {
      document.documentElement.dataset.pomelaGate = 'maintenance';
    } catch {
      /* noop */
    }
    if (typeof window === 'undefined') return;
    try {
      if (window.matchMedia('(pointer: coarse)').matches) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    } catch {
      /* fall through */
    }
    const wrap = markWrapRef.current;
    if (!wrap) return;
    let raf = 0;
    const MAX_TILT_DEG = 6;
    function onMove(e: MouseEvent) {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        if (!wrap) return;
        const cx = window.innerWidth / 2;
        const cy = window.innerHeight / 2;
        const nx = Math.max(-1, Math.min(1, (e.clientX - cx) / cx));
        const ny = Math.max(-1, Math.min(1, (e.clientY - cy) / cy));
        wrap.style.setProperty('--mag-x', `${(nx * MAX_TILT_DEG).toFixed(2)}deg`);
        wrap.style.setProperty('--mag-y', `${(-ny * MAX_TILT_DEG).toFixed(2)}deg`);
      });
    }
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => {
      window.removeEventListener('pointermove', onMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Scroll-triggered reveals. We ARM the hidden state from JS only when motion
  // is allowed — so under prefers-reduced-motion (or no JS / no IO) every
  // section stays fully visible. WCAG-safe by construction.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try { if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return; } catch { return; }
    if (!('IntersectionObserver' in window)) return;
    const root = document.querySelector('.lp-root') as HTMLElement | null;
    if (!root) return;
    root.dataset.revealArmed = '1';
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) { e.target.classList.add('lp-in'); io.unobserve(e.target); }
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.12 }
    );
    document.querySelectorAll('.lp-reveal').forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  async function joinList(e: Event) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !isValidEmail(trimmed)) {
      setErrMsg(t('lp.errEmail'));
      setStatus('error');
      try { navigator.vibrate?.(12); } catch { /* no haptics */ }
      return;
    }
    if (status === 'loading' || status === 'success') return;
    setStatus('loading');

    recordWaitlist(trimmed); // local backup so a flaky network never loses the lead

    const minDelay = new Promise<void>((resolve) => setTimeout(resolve, 550));
    const attribution = captureAttribution();
    const formspreePayload = { email: trimmed, ...attribution };

    const formspreePromise = fetch(WAITLIST_ENDPOINT, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(formspreePayload)
    })
      .then((r) => r.ok)
      .catch(() => false);

    // Honest success: only celebrate if a REAL pipe actually captured the lead —
    // the gateway persisted it to waitlist_signups (persisted:true) OR Formspree
    // accepted it. If both fail, show an error (fail-closed) — never a fake ✓.
    let persisted = false;
    let formspreeOk = false;
    try {
      formspreeOk = await formspreePromise;
      try {
        const r = await fetch(WAITLIST_PERSIST_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: trimmed, ...attribution, formspree_ok: formspreeOk })
        });
        if (r.ok) {
          const j = (await r.json().catch(() => null)) as { persisted?: boolean } | null;
          persisted = !!j?.persisted;
        }
      } catch { /* network — fall through to the pipe check */ }
    } catch { /* both pipes errored */ }

    await minDelay;
    if (persisted || formspreeOk) {
      setStatus('success');
      try { navigator.vibrate?.([18, 40, 14]); } catch { /* no haptics */ }
    } else {
      setErrMsg(t('lp.errNetwork'));
      setStatus('error');
      try { navigator.vibrate?.(12); } catch { /* no haptics */ }
    }
  }

  // The waitlist form — reused in the hero and the closing CTA.
  const waitlistForm = (idSuffix: string): JSX.Element => (
    <form className="lp-form" onSubmit={joinList} noValidate>
      <input
        ref={idSuffix === 'hero' ? emailRef : undefined}
        type="email"
        inputMode="email"
        autoComplete="email"
        placeholder="you@domain.com"
        value={email}
        onInput={(e) => {
          setEmail((e.target as HTMLInputElement).value);
          if (status === 'error') setStatus('idle');
        }}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            joinList(e as unknown as Event);
          }
        }}
        className="lp-input"
        aria-label={t('lp.formAriaEmail')}
        disabled={status === 'loading' || status === 'success'}
        required
      />
      <button
        type="submit"
        className={`lp-cta${status === 'success' ? ' lp-cta--success' : ''}`}
        disabled={status === 'loading' || status === 'success' || email.length === 0}
      >
        <span className="mg-cta-label lp-cta-label">
          {status === 'loading'
            ? t('lp.formSending')
            : status === 'success'
              ? t('lp.formSuccess')
              : t('lp.navCta')}
        </span>
        {status === 'idle' && (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M5 12h14" />
            <path d="M13 6l6 6-6 6" />
          </svg>
        )}
      </button>
      {status === 'error' && (
        <div className="lp-error" role="alert">{errMsg}</div>
      )}
    </form>
  );

  return (
    <main className="mg-root lp-root" aria-label={t('lp.ariaRoot')}>
      <style>{LANDING_CSS}</style>

      <div className="lp-bloom" aria-hidden="true" />
      <div className="lp-grain" aria-hidden="true" />

      {/* ── Nav ─────────────────────────────────────────────────────────── */}
      <nav className="lp-nav" aria-label={t('lp.ariaPrimary')}>
        <button type="button" className="lp-nav-brand" onClick={onWordmarkTap} aria-label="Pomela">
          <span className="lp-nav-mark" aria-hidden="true">
            <BrandMark size={26} />
          </span>
          Pomela
        </button>
        <a className="lp-nav-cta" href="#waitlist">{t('lp.navCta')}</a>
      </nav>

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <header className="lp-hero" id="waitlist">
        <div className="lp-hero-copy">
          <span className="lp-eyebrow">{t('lp.heroEyebrow')}</span>
          <h1 className="mg-title lp-title" dangerouslySetInnerHTML={{ __html: t('lp.heroTitle') }} />
          <p className="mg-sub lp-sub">{t('lp.heroSub')}</p>
          {waitlistForm('hero')}
          {status !== 'success' && (
            <p className="lp-trust" aria-live="polite">{t('lp.heroTrust')}</p>
          )}
        </div>

        <div className="lp-hero-art" aria-hidden="true">
          <div className="mg-mark-wrap lp-mark-wrap" ref={markWrapRef}>
            <span className="lp-halo" />
            <div className="lp-ring lp-ring--a" />
            <div className="lp-ring lp-ring--b" />
            <div className="lp-ring lp-ring--c" />
            <BrandMark size={148} className="lp-mark" />
          </div>
        </div>
      </header>

      <div className="lp-band" aria-hidden="true"><span className="lp-band-fill" /></div>

      {/* ── How it works — three steps, no apps shown ───────────────────── */}
      <section className="lp-section lp-reveal">
        <SectionHead
          eyebrow={t('lp.heroEyebrow')}
          title={t('lp.s1Title')}
          sub={t('lp.s1Sub')}
        />
        <div className="lp-steps">
          <StepCard
            n="01"
            kind={t('lp.step1Kind')}
            title={t('lp.step1Title')}
            body={t('lp.step1Body')}
          />
          <StepCard
            n="02"
            kind={t('lp.step2Kind')}
            title={t('lp.step2Title')}
            body={t('lp.step2Body')}
          />
          <StepCard
            n="03"
            kind={t('lp.step3Kind')}
            title={t('lp.step3Title')}
            body={t('lp.step3Body')}
          />
        </div>
      </section>

      {/* ── Use-cases (the beachhead) ───────────────────────────────────── */}
      <section className="lp-section lp-section--tint lp-reveal">
        <SectionHead
          eyebrow={t('lp.s2Eyebrow')}
          title={t('lp.s2Title')}
          sub={t('lp.s2Sub')}
        />
        <div className="lp-cards">
          <UseCard
            accent="#B08968"
            kind={t('lp.uc1Kind')}
            title={t('lp.uc1Title')}
            body={t('lp.uc1Body')}
          />
          <UseCard
            accent="#2F7D4F"
            kind={t('lp.uc2Kind')}
            title={t('lp.uc2Title')}
            body={t('lp.uc2Body')}
          />
          <UseCard
            accent="#7C4DFF"
            kind={t('lp.uc3Kind')}
            title={t('lp.uc3Title')}
            body={t('lp.uc3Body')}
          />
        </div>
      </section>

      {/* ── Differentiators ─────────────────────────────────────────────── */}
      <section className="lp-section lp-reveal">
        <SectionHead
          eyebrow={t('lp.s3Eyebrow')}
          title={t('lp.s3Title')}
          sub={t('lp.s3Sub')}
        />
        <div className="lp-diffs">
          <DiffRow
            icon="install"
            title={t('lp.d1Title')}
            body={t('lp.d1Body')}
          />
          <DiffRow
            icon="wand"
            title={t('lp.d2Title')}
            body={t('lp.d2Body')}
          />
          <DiffRow
            icon="bolt"
            title={t('lp.d3Title')}
            body={t('lp.d3Body')}
          />
          <DiffRow
            icon="export"
            title={t('lp.d4Title')}
            body={t('lp.d4Body')}
          />
        </div>
      </section>

      {/* ── Honest social proof / build-in-public ───────────────────────── */}
      <section className="lp-section lp-section--tint lp-reveal">
        <div className="lp-bip">
          <span className="lp-eyebrow">{t('lp.bipEyebrow')}</span>
          <h2 className="lp-bip-title">{t('lp.bipTitle')}</h2>
          <p className="lp-bip-body">{t('lp.bipBody')}</p>
          <div className="lp-bip-proof">
            <div className="lp-bip-stat">
              <span className="lp-bip-num">{t('lp.bipStat1Num')}</span>
              <span className="lp-bip-lbl" dangerouslySetInnerHTML={{ __html: t('lp.bipStat1Lbl') }} />
            </div>
            <div className="lp-bip-stat">
              <span className="lp-bip-num">{t('lp.bipStat2Num')}</span>
              <span className="lp-bip-lbl" dangerouslySetInnerHTML={{ __html: t('lp.bipStat2Lbl') }} />
            </div>
            <div className="lp-bip-stat">
              <span className="lp-bip-num">{t('lp.bipStat3Num')}</span>
              <span className="lp-bip-lbl" dangerouslySetInnerHTML={{ __html: t('lp.bipStat3Lbl') }} />
            </div>
          </div>
          <p className="lp-bip-note" dangerouslySetInnerHTML={{ __html: t('lp.bipNote') }} />
        </div>
      </section>

      {/* ── Final CTA ───────────────────────────────────────────────────── */}
      <section className="lp-section lp-final lp-reveal">
        <h2 className="lp-final-title">{t('lp.finalTitle')}</h2>
        <p className="lp-final-sub">
{t('lp.finalSub')}
        </p>
        {waitlistForm('final')}
        {status !== 'success' && (
          <p className="lp-trust" aria-live="polite">{t('lp.finalTrust')}</p>
        )}
      </section>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <footer className="lp-footer">
        <span className="lp-foot-brand"><BrandMark size={18} /> Pomela</span>
        <nav className="lp-foot-links" aria-label={t('lp.ariaLegal')}>
          <a href="/legal/privacy">{t('lp.footPrivacy')}</a>
          <span aria-hidden="true">·</span>
          <a href="/legal/terms">{t('lp.footTerms')}</a>
          <span aria-hidden="true">·</span>
          <a href="/legal/security">{t('lp.footSecurity')}</a>
        </nav>
        <span className="lp-foot-tag">{t('lp.footTag')}</span>
      </footer>
    </main>
  );
}

// ─── pieces ───────────────────────────────────────────────────────────────

function SectionHead(props: { eyebrow: string; title: string; sub: string }): JSX.Element {
  return (
    <div className="lp-head">
      <span className="lp-eyebrow">{props.eyebrow}</span>
      <h2 className="lp-head-title" dangerouslySetInnerHTML={{ __html: props.title }} />
      <p className="lp-head-sub" dangerouslySetInnerHTML={{ __html: props.sub }} />
    </div>
  );
}

function UseCard(props: { accent: string; kind: string; title: string; body: string }): JSX.Element {
  return (
    <div className="lp-card">
      <span className="lp-card-dot" style={{ background: props.accent }} aria-hidden="true" />
      <span className="lp-card-kind" style={{ color: props.accent }}>{props.kind}</span>
      <h3 className="lp-card-title">{props.title}</h3>
      <p className="lp-card-body">{props.body}</p>
    </div>
  );
}

function DiffRow(props: { icon: 'install' | 'wand' | 'bolt' | 'export'; title: string; body: string }): JSX.Element {
  return (
    <div className="lp-diff">
      <span className="lp-diff-icon" aria-hidden="true"><DiffIcon name={props.icon} /></span>
      <div>
        <h3 className="lp-diff-title">{props.title}</h3>
        <p className="lp-diff-body">{props.body}</p>
      </div>
    </div>
  );
}

/** A non-app "how it works" step — typographic only, no mockups. */
function StepCard(props: { n: string; kind: string; title: string; body: string }): JSX.Element {
  return (
    <div className="lp-step">
      <span className="lp-step-n" aria-hidden="true">{props.n}</span>
      <span className="lp-step-kind">{props.kind}</span>
      <h3 className="lp-step-title">{props.title}</h3>
      <p className="lp-step-body" dangerouslySetInnerHTML={{ __html: props.body }} />
    </div>
  );
}

function BrandMark(props: { size: number; className?: string }): JSX.Element {
  const id = 'bm' + props.size;
  return (
    <svg className={props.className} width={props.size} height={props.size} viewBox="0 0 512 512"
      role="img" aria-label="Pomela" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={`${id}-tile`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stop-color="#FF9F5A" />
          <stop offset="0.52" stop-color="#F4477E" />
          <stop offset="1" stop-color="#7C4DFF" />
        </linearGradient>
        <radialGradient id={`${id}-sheen`} cx="30%" cy="22%" r="78%">
          <stop offset="0" stop-color="#FFFFFF" stop-opacity="0.38" />
          <stop offset="0.62" stop-color="#FFFFFF" stop-opacity="0" />
        </radialGradient>
        <mask id={`${id}-cut`}>
          <rect width="512" height="512" fill="#000" />
          <rect x="141" y="124" width="52" height="276" rx="26" fill="#fff" />
          <circle cx="263" cy="224" r="108" fill="#fff" />
          <circle cx="263" cy="224" r="50" fill="#000" />
          <circle cx="281" cy="208" r="21" fill="#fff" />
        </mask>
      </defs>
      <rect width="512" height="512" rx="112" fill={`url(#${id}-tile)`} />
      <rect width="512" height="512" rx="112" fill={`url(#${id}-sheen)`} />
      <rect width="512" height="512" fill="#fff" mask={`url(#${id}-cut)`} />
    </svg>
  );
}

function DiffIcon(props: { name: 'install' | 'wand' | 'bolt' | 'export' }): JSX.Element {
  const common = { width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': 2, 'stroke-linecap': 'round' as const, 'stroke-linejoin': 'round' as const };
  if (props.name === 'install') return (<svg {...common}><rect x="6" y="2" width="12" height="20" rx="3" /><path d="M12 7v7" /><path d="m9 11 3 3 3-3" /></svg>);
  if (props.name === 'wand') return (<svg {...common}><path d="m3 21 12-12" /><path d="M15 5l4 4" /><path d="M17 3v0M21 7v0M19 11v0" /></svg>);
  if (props.name === 'bolt') return (<svg {...common}><path d="M13 2 4 14h7l-1 8 9-12h-7l1-8Z" /></svg>);
  return (<svg {...common}><path d="M4 12v6a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-6" /><path d="M12 15V3" /><path d="m8 7 4-4 4 4" /></svg>);
}

const LANDING_CSS = `
.lp-root {
  position: relative;
  min-height: 100dvh;
  width: 100%;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  display: block;
  padding: 0;
  background: radial-gradient(120% 70% at 50% -10%, #170B15 0%, #0A0A0A 55%);
  color: #FAFAFA;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  isolation: isolate;
  animation: lp-fade 480ms cubic-bezier(0.22, 1, 0.36, 1) both;
}
@keyframes lp-fade { from { opacity: 0; } to { opacity: 1; } }

.lp-bloom {
  position: absolute; top: -8%; left: 50%; width: min(820px, 130vw); aspect-ratio: 1;
  transform: translateX(-50%);
  background:
    radial-gradient(closest-side, rgba(255,159,90,0.26), transparent 70%),
    radial-gradient(closest-side at 62% 48%, rgba(244,71,126,0.26), transparent 72%),
    radial-gradient(closest-side at 38% 60%, rgba(124,77,255,0.24), transparent 75%);
  filter: blur(54px) saturate(120%); opacity: 0.85; pointer-events: none; z-index: 0;
  animation: lp-breathe 8s ease-in-out infinite;
}
@keyframes lp-breathe { 0%,100% { opacity: .72; transform: translateX(-50%) scale(1); } 50% { opacity: 1; transform: translateX(-50%) scale(1.05); } }
.lp-grain {
  position: absolute; inset: 0; pointer-events: none; mix-blend-mode: overlay; opacity: 0.06; z-index: 1;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
}

/* shared layout */
.lp-section { position: relative; z-index: 2; max-width: 1080px; margin: 0 auto; padding: 72px 24px; }
.lp-section--tint { background: rgba(255,255,255,0.018); border-top: 1px solid rgba(255,255,255,0.05); border-bottom: 1px solid rgba(255,255,255,0.05); max-width: none; }
.lp-section--tint > * { max-width: 1080px; margin-left: auto; margin-right: auto; }
.lp-eyebrow { display: inline-block; font-size: 12.5px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: #F4477E; margin-bottom: 14px; }

/* nav */
.lp-nav {
  position: sticky; top: 0; z-index: 50; display: flex; align-items: center; justify-content: space-between;
  max-width: 1080px; margin: 0 auto; padding: 16px 24px;
  backdrop-filter: blur(14px); -webkit-backdrop-filter: blur(14px);
}
.lp-nav-brand { display: inline-flex; align-items: center; gap: 9px; appearance: none; background: transparent; border: 0; padding: 4px; cursor: default; user-select: none; -webkit-tap-highlight-color: transparent; color: #FAFAFA; font-family: inherit; font-size: 19px; font-weight: 600; letter-spacing: -0.025em; }
.lp-nav-mark { display: inline-grid; place-items: center; }
.lp-nav-mark svg { border-radius: 7px; }
.lp-nav-cta { display: inline-flex; align-items: center; min-height: 44px; font-size: 14px; font-weight: 600; color: #FAFAFA; text-decoration: none; padding: 9px 16px; border-radius: 11px; border: 1px solid rgba(255,255,255,0.14); background: rgba(255,255,255,0.04); transition: background 180ms ease, transform 180ms ease; }
.lp-nav-cta:hover { background: rgba(255,255,255,0.09); transform: translateY(-1px); }

/* hero */
.lp-hero { position: relative; z-index: 2; max-width: 1080px; margin: 0 auto; padding: 56px 24px 40px; display: grid; grid-template-columns: 1.05fr 0.95fr; gap: 40px; align-items: center; scroll-margin-top: 80px; }
.lp-title { margin: 0 0 18px; font-size: clamp(34px, 6vw, 60px); font-weight: 680; letter-spacing: -0.035em; line-height: 1.02; background: linear-gradient(180deg, #FFFFFF 0%, #D9D4DC 100%); -webkit-background-clip: text; background-clip: text; color: transparent; }
.lp-title-accent { background: linear-gradient(120deg, #FF9F5A, #F4477E 55%, #7C4DFF); -webkit-background-clip: text; background-clip: text; color: transparent; }
.lp-sub { margin: 0 0 26px; max-width: 36ch; font-size: 16.5px; line-height: 1.6; color: #B7B2BC; }

/* waitlist form */
.lp-form { display: flex; flex-wrap: wrap; gap: 9px; width: 100%; max-width: 460px; }
.lp-input { flex: 1 1 200px; min-width: 0; height: 50px; box-sizing: border-box; padding: 0 16px; border-radius: 14px; border: 1px solid rgba(255,255,255,0.10); background: rgba(255,255,255,0.04); color: #FAFAFA; font-family: inherit; font-size: 16px; font-weight: 500; letter-spacing: -0.01em; -webkit-appearance: none; appearance: none; transition: border-color 200ms ease, background 200ms ease, box-shadow 200ms ease; }
.lp-input::placeholder { color: #71717A; }
.lp-input:focus { outline: none; border-color: rgba(244,71,126,0.65); background: rgba(255,255,255,0.07); box-shadow: 0 0 0 4px rgba(244,71,126,0.16); }
.lp-input:disabled { opacity: 0.7; }
.lp-cta { display: inline-flex; align-items: center; justify-content: center; gap: 8px; flex: 0 0 auto; min-width: 156px; height: 50px; padding: 0 20px; border-radius: 14px; border: 0; background: linear-gradient(135deg, #FF9F5A 0%, #F4477E 52%, #7C4DFF 100%); color: #fff; font-family: inherit; font-size: 15px; font-weight: 600; letter-spacing: -0.01em; cursor: pointer; box-shadow: 0 12px 30px -10px rgba(244,71,126,0.6), 0 1px 0 rgba(255,255,255,0.12) inset; transition: transform 180ms cubic-bezier(0.34,1.56,0.64,1), box-shadow 180ms ease, opacity 200ms ease; }
.lp-cta:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 16px 38px -12px rgba(244,71,126,0.7), 0 1px 0 rgba(255,255,255,0.14) inset; }
.lp-cta:active:not(:disabled) { transform: translateY(0) scale(0.98); }
.lp-cta:disabled { opacity: 0.6; cursor: not-allowed; }
.lp-cta--success { background: linear-gradient(135deg, #10B981, #34D399 60%, #10B981); opacity: 1 !important; cursor: default; pointer-events: none; animation: lp-pop 360ms cubic-bezier(0.34,1.56,0.64,1) both; }
@keyframes lp-pop { from { transform: scale(0.94); } to { transform: scale(1); } }
.lp-error { flex: 1 0 100%; padding: 0 4px; font-size: 13px; color: #FB7185; }
/* §AA-4.5 — #71717A on this near-black ground measures 4.05:1 at 12.5px, under the 4.5 floor for
   normal text. Measured from the rendered framebuffer of the live landing page, in English and
   Hebrew, at every viewport. #8B8B96 is the same muted register at 5.86:1. */
.lp-trust { margin: 14px 0 0; font-size: 12.5px; font-weight: 500; color: #8B8B96; }

/* hero art, the ONLY hero visual is the centered Pomela brand mark (no apps) */
.lp-hero-art { position: relative; display: grid; place-items: center; min-height: 460px; }
.lp-mark-wrap { position: relative; display: grid; place-items: center; width: 200px; height: 200px; --mag-x: 0deg; --mag-y: 0deg; transform: perspective(900px) rotateY(var(--mag-x)) rotateX(var(--mag-y)); transition: transform 240ms cubic-bezier(0.22,1,0.36,1); will-change: transform; z-index: 3; }
.lp-halo { position: absolute; inset: 0; margin: auto; width: 300px; height: 300px; border-radius: 50%; pointer-events: none; z-index: 0; background: radial-gradient(closest-side, rgba(244,71,126,0.30), rgba(124,77,255,0.12) 56%, transparent 72%); filter: blur(6px); animation: lp-breathe 8s ease-in-out infinite; }
.lp-mark { position: relative; z-index: 3; border-radius: 34px; filter: drop-shadow(0 30px 64px rgba(244,71,126,0.5)); animation: lp-mark-breathe 4.6s cubic-bezier(0.4,0,0.6,1) infinite; }
@keyframes lp-mark-breathe { 0%,100% { transform: scale(1) translateY(0); } 50% { transform: scale(1.035) translateY(-3px); } }
.lp-ring { position: absolute; inset: 0; margin: auto; border-radius: 50%; pointer-events: none; z-index: 1; }
.lp-ring--a { width: 214px; height: 214px; border: 1px solid rgba(244,71,126,0.24); animation: lp-ring 3.6s cubic-bezier(0.22,1,0.36,1) infinite; }
.lp-ring--b { width: 274px; height: 274px; border: 1px solid rgba(124,77,255,0.17); animation: lp-ring 3.6s cubic-bezier(0.22,1,0.36,1) infinite 1.2s; }
.lp-ring--c { width: 338px; height: 338px; border: 1px solid rgba(255,159,90,0.12); animation: lp-ring 3.6s cubic-bezier(0.22,1,0.36,1) infinite 2.4s; }
@keyframes lp-ring { 0% { opacity: 0; transform: scale(0.82); } 35% { opacity: 0.7; } 100% { opacity: 0; transform: scale(1.12); } }

/* how it works, three typographic steps, zero app mockups */
.lp-steps { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; }
.lp-step { position: relative; padding: 28px 24px; border-radius: 18px; background: rgba(255,255,255,0.025); border: 1px solid rgba(255,255,255,0.07); overflow: hidden; transition: transform 220ms cubic-bezier(0.22,1,0.36,1), background 220ms ease, border-color 220ms ease; }
.lp-step:hover { transform: translateY(-3px); background: rgba(255,255,255,0.045); border-color: rgba(255,255,255,0.12); }
.lp-step-n { display: block; font-size: 13px; font-weight: 700; letter-spacing: 0.14em; background: linear-gradient(120deg, #FF9F5A, #F4477E 55%, #7C4DFF); -webkit-background-clip: text; background-clip: text; color: transparent; }
.lp-step-kind { display: block; margin-top: 14px; font-size: 12px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #FB7DA6; }
.lp-step-title { margin: 7px 0 8px; font-size: 18px; font-weight: 680; letter-spacing: -0.02em; color: #FAFAFA; }
.lp-step-body { margin: 0; font-size: 14.5px; line-height: 1.6; color: #A1A1AA; }

/* scroll reveal, JS arms [data-reveal-armed] ONLY when motion is allowed, so
   reduced-motion / no-JS keep every section visible (WCAG-safe by construction) */
.lp-root[data-reveal-armed] .lp-reveal { opacity: 0; transform: translateY(26px); transition: opacity 660ms cubic-bezier(0.22,1,0.36,1), transform 660ms cubic-bezier(0.22,1,0.36,1); will-change: opacity, transform; }
.lp-root[data-reveal-armed] .lp-reveal.lp-in { opacity: 1; transform: none; }

/* section heads */
.lp-head { text-align: center; max-width: 680px; margin: 0 auto 44px; }
.lp-head-title { margin: 0 0 12px; font-size: clamp(26px, 4vw, 38px); font-weight: 680; letter-spacing: -0.03em; line-height: 1.08; color: #FAFAFA; }
.lp-head-sub { margin: 0; font-size: 16px; line-height: 1.6; color: #A1A1AA; }

/* use cards */
.lp-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; }
.lp-card { position: relative; padding: 26px 22px; border-radius: 18px; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.07); overflow: hidden; transition: transform 220ms cubic-bezier(0.22,1,0.36,1), background 220ms ease, border-color 220ms ease; }
.lp-card:hover { transform: translateY(-3px); background: rgba(255,255,255,0.05); border-color: rgba(255,255,255,0.12); }
.lp-card-dot { position: absolute; top: 0; left: 0; width: 100%; height: 3px; opacity: 0.9; }
.lp-card-kind { font-size: 12px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; }
.lp-card-title { margin: 10px 0 8px; font-size: 19px; font-weight: 680; letter-spacing: -0.02em; color: #FAFAFA; }
.lp-card-body { margin: 0; font-size: 14.5px; line-height: 1.6; color: #A1A1AA; }

/* diffs */
.lp-diffs { display: grid; grid-template-columns: 1fr 1fr; gap: 22px 36px; max-width: 920px; margin: 0 auto; }
.lp-diff { display: flex; gap: 16px; align-items: flex-start; }
.lp-diff-icon { flex-shrink: 0; display: grid; place-items: center; width: 44px; height: 44px; border-radius: 13px; background: linear-gradient(135deg, rgba(255,159,90,0.18), rgba(244,71,126,0.18) 55%, rgba(124,77,255,0.18)); color: #FFC7DB; border: 1px solid rgba(255,255,255,0.08); }
.lp-diff-title { margin: 2px 0 7px; font-size: 17px; font-weight: 670; letter-spacing: -0.02em; color: #FAFAFA; }
.lp-diff-body { margin: 0; font-size: 14.5px; line-height: 1.6; color: #A1A1AA; }

/* build in public */
.lp-bip { text-align: center; max-width: 760px; margin: 0 auto; }
.lp-bip-title { margin: 0 0 16px; font-size: clamp(24px, 3.6vw, 34px); font-weight: 680; letter-spacing: -0.03em; line-height: 1.12; color: #FAFAFA; }
.lp-bip-body { margin: 0 auto 30px; max-width: 60ch; font-size: 16px; line-height: 1.62; color: #A1A1AA; }
.lp-bip-proof { display: flex; flex-wrap: wrap; justify-content: center; gap: 18px; margin-bottom: 26px; }
.lp-bip-stat { flex: 1 1 180px; max-width: 220px; padding: 22px 18px; border-radius: 16px; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.07); }
.lp-bip-num { display: block; font-size: 30px; font-weight: 700; letter-spacing: -0.03em; background: linear-gradient(120deg, #FF9F5A, #F4477E 55%, #7C4DFF); -webkit-background-clip: text; background-clip: text; color: transparent; }
.lp-bip-lbl { display: block; margin-top: 6px; font-size: 13px; line-height: 1.45; color: #9A9AA2; }
.lp-bip-note { margin: 0; font-size: 14px; line-height: 1.6; color: #8E8E96; font-style: italic; }
.lp-bip-note a, .lp-final a, .lp-bip a { color: #FB7DA6; text-decoration: none; font-weight: 600; }
.lp-bip-note a:hover { text-decoration: underline; }

/* final */
.lp-final { text-align: center; max-width: 620px; }
.lp-final-title { margin: 0 0 12px; font-size: clamp(28px, 4vw, 40px); font-weight: 700; letter-spacing: -0.03em; color: #FAFAFA; }
.lp-final-sub { margin: 0 auto 26px; max-width: 44ch; font-size: 16px; line-height: 1.6; color: #A1A1AA; }
.lp-final .lp-form { margin: 0 auto; justify-content: center; }
.lp-final .lp-trust { text-align: center; }

/* footer */
.lp-footer { position: relative; z-index: 2; display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 14px; max-width: 1080px; margin: 0 auto; padding: 30px 24px calc(env(safe-area-inset-bottom, 0px) + 36px); border-top: 1px solid rgba(255,255,255,0.06); }
.lp-foot-brand { display: inline-flex; align-items: center; gap: 8px; font-size: 15px; font-weight: 600; letter-spacing: -0.02em; color: #D4D4D8; }
.lp-foot-brand svg { border-radius: 5px; }
.lp-foot-links { display: inline-flex; align-items: center; gap: 4px; font-size: 13.5px; color: #71717A; }
/* §WS2, 44px touch target for the legal links (were ~20px tall). Padding grows
   the hit area; the gap shrinks to keep the row visually tight. */
.lp-foot-links a { display: inline-flex; align-items: center; min-height: 44px; padding: 0 8px; color: #A1A1AA; text-decoration: none; transition: color 160ms ease; }
.lp-foot-links a:hover { color: #FAFAFA; }
.lp-foot-tag { font-size: 13px; color: #5A5A62; letter-spacing: -0.01em; }

/* band */
.lp-band { position: relative; z-index: 2; width: 88px; height: 3px; margin: 8px auto 0; border-radius: 999px; background: rgba(244,71,126,0.16); overflow: hidden; }
.lp-band-fill { position: absolute; inset: 0; width: 42%; border-radius: inherit; background: linear-gradient(90deg, #FF9F5A, #F4477E, #7C4DFF); animation: lp-scan 1.8s cubic-bezier(0.4,0,0.2,1) infinite; }
@keyframes lp-scan { 0% { transform: translateX(-110%); } 100% { transform: translateX(270%); } }

/* responsive */
@media (max-width: 900px) {
  .lp-hero { grid-template-columns: 1fr; gap: 20px; padding-top: 28px; text-align: center; }
  .lp-hero-copy { display: flex; flex-direction: column; align-items: center; }
  .lp-hero-art { min-height: 300px; order: -1; }
  .lp-mark-wrap { width: 160px; height: 160px; }
  .lp-halo { width: 240px; height: 240px; }
  .lp-ring--a { width: 176px; height: 176px; }
  .lp-ring--b { width: 224px; height: 224px; }
  .lp-ring--c { width: 276px; height: 276px; }
  .lp-steps { grid-template-columns: 1fr; }
  .lp-diffs { grid-template-columns: 1fr; }
}
@media (max-width: 760px) {
  .lp-steps { grid-template-columns: 1fr; gap: 14px; }
  .lp-cards { grid-template-columns: 1fr; }
  .lp-section { padding: 56px 22px; }
}
@media (max-width: 420px) {
  .lp-form { flex-direction: column; }
  .lp-input { flex: 0 0 50px; width: 100%; }
  .lp-cta { width: 100%; }
  .lp-nav-cta { display: none; }
}
@media (prefers-reduced-motion: reduce) {
  .lp-root, .lp-bloom, .lp-halo, .lp-mark, .lp-ring, .lp-band-fill, .lp-cta--success { animation: none !important; }
  .lp-mark-wrap { transition: none !important; }
  .lp-card:hover, .lp-step:hover { transform: none; }
  /* reveals never armed under reduced-motion, but belt-and-suspenders: */
  .lp-reveal { opacity: 1 !important; transform: none !important; }
}
`;
